import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { toast } from "sonner";
import {
  MapPin, Calendar, Globe, Phone, Building2, Trees, Users,
  Heart, ExternalLink, Trophy, GraduationCap, ShieldCheck, Pencil, Send,
} from "lucide-react";
import AppLayout from "../components/AppLayout";
import SportConfigsForm from "../components/SportConfigsForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useSeoHead from "../hooks/useSeoHead";

// Fix default marker icons (CRA / webpack break Leaflet's icon paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const KIND_ROUTE = { match: "/matches", tournament: "/tournaments", league: "/leagues", lesson: "/lessons" };
const KIND_ICON = { match: Trophy, tournament: Trophy, league: Calendar, lesson: GraduationCap };

export default function FacilityDetail() {
  const { idOrSlug } = useParams();
  const { user } = useAuth();
  const [facility, setFacility] = useState(null);
  const [events, setEvents] = useState([]);
  const [hostedClubs, setHostedClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [claim, setClaim] = useState(null); // existing claim by current user
  const [claimOpen, setClaimOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, ev, ch] = await Promise.all([
        api.get(`/facilities/${idOrSlug}`).then((r) => r.data),
        api.get(`/facilities/${idOrSlug}/upcoming`).then((r) => r.data).catch(() => []),
        api.get(`/facilities/${idOrSlug}/clubs`).then((r) => r.data?.clubs || []).catch(() => []),
      ]);
      setFacility(f);
      setEvents(Array.isArray(ev) ? ev : []);
      setHostedClubs(ch);
      try {
        const { data } = await api.get(`/facilities/${f.id}/follow-status`);
        setFollowing(!!data.following);
      } catch { setFollowing(false); }
      // My claim status (auth required)
      if (user) {
        try {
          const { data } = await api.get(`/facilities/${f.id}/my-claim`);
          setClaim(data && data.id ? data : null);
        } catch { setClaim(null); }
      } else {
        setClaim(null);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load facility");
      setFacility(null);
    } finally {
      setLoading(false);
    }
  }, [idOrSlug, user]);

  useEffect(() => { load(); }, [load]);

  const toggleFollow = async () => {
    if (!user) {
      sessionStorage.setItem("post_login_redirect", `/facilities/${idOrSlug}`);
      window.location.href = "/login";
      return;
    }
    setFollowBusy(true);
    // Optimistic UI — flip state + adjust count immediately, revert on error.
    const willFollow = !following;
    setFollowing(willFollow);
    setFacility((f) => f ? { ...f, followers: Math.max(0, (f.followers || 0) + (willFollow ? 1 : -1)) } : f);
    try {
      if (willFollow) {
        await api.post(`/facilities/${facility.id}/follow`);
        toast.success("Following — you'll be notified when new events post here");
      } else {
        await api.delete(`/facilities/${facility.id}/follow`);
        toast.success("Unfollowed");
      }
    } catch (e) {
      // Revert
      setFollowing(!willFollow);
      setFacility((f) => f ? { ...f, followers: Math.max(0, (f.followers || 0) + (willFollow ? -1 : 1)) } : f);
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setFollowBusy(false);
    }
  };

  const isOwner = user && facility?.owner_id === user.id;
  const isAdmin = user?.role === "admin";
  const canClaim = user && !facility?.owner_id && !isAdmin && (user.role === "club_admin" || user.role === "coach" || user.role === "promoter" || user.role === "federation");
  const claimPending = claim && claim.status === "pending";

  // Hooks must be unconditional — compute the map position before any early returns
  const hasGeo = facility?.lat != null && facility?.lng != null;
  const mapPos = React.useMemo(
    () => (hasGeo ? [facility.lat, facility.lng] : null),
    [hasGeo, facility?.lat, facility?.lng],
  );

  // SEO: title, description, canonical + SportsActivityLocation JSON-LD.
  // Emitted regardless of loading state so the early-return skeleton still
  // has meta — crawlers never hit the skeleton in practice but defence-in-depth.
  const canonical = typeof window !== "undefined" && facility
    ? `${window.location.origin}/facilities/${facility.slug || facility.id}`
    : undefined;
  const seoDescription = facility
    ? `${facility.name}${facility.city ? ` in ${facility.city}${facility.country ? `, ${facility.country}` : ""}` : ""}. ${facility.num_courts ? `${facility.num_courts} courts · ` : ""}${(facility.sport_slugs || []).join(", ") || "Sports venue"}${facility.indoor === true ? " · Indoor" : facility.indoor === false ? " · Outdoor" : ""}. Book, follow, and discover upcoming events on Pick2Play.`
    : undefined;
  const jsonLd = React.useMemo(() => {
    if (!facility) return null;
    const sportMap = { pickleball: "Pickleball", padel: "Padel", tennis: "Tennis", golf: "Golf", basketball: "Basketball", football: "Soccer", volleyball: "Volleyball" };
    const sport = (facility.sport_slugs && facility.sport_slugs[0]) ? sportMap[facility.sport_slugs[0]] || facility.sport_slugs[0] : "Sport";
    return {
      "@context": "https://schema.org",
      "@type": "SportsActivityLocation",
      name: facility.name,
      sport,
      url: canonical,
      image: facility.photo_urls?.[0] || undefined,
      description: facility.description || seoDescription,
      address: {
        "@type": "PostalAddress",
        streetAddress: facility.address || undefined,
        addressLocality: facility.city || undefined,
        addressCountry: facility.country || undefined,
      },
      ...(hasGeo ? { geo: { "@type": "GeoCoordinates", latitude: facility.lat, longitude: facility.lng } } : {}),
      ...(facility.phone ? { telephone: facility.phone } : {}),
      ...(facility.website ? { sameAs: [facility.website] } : {}),
      ...(facility.opening_hours ? { openingHours: facility.opening_hours } : {}),
    };
  }, [facility, canonical, hasGeo, seoDescription]);
  useSeoHead({
    title: facility ? `${facility.name}${facility.city ? ` — ${facility.city}` : ""} · Pick2Play` : "Pick2Play",
    description: seoDescription,
    canonical,
    image: facility?.photo_urls?.[0],
    type: "place",
    jsonLd,
  });

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto pp-card-elevated p-10 mt-8 animate-pulse">
          <div className="h-8 w-2/3 bg-white/10 rounded mb-4" />
          <div className="h-4 w-full bg-white/5 rounded mb-2" />
          <div className="h-4 w-1/2 bg-white/5 rounded" />
        </div>
      </AppLayout>
    );
  }

  if (!facility) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto pp-card-elevated p-10 mt-12 text-center" data-testid="facility-not-found">
          <div className="text-[#FF3B30] font-bold mb-2">Facility not found</div>
          <Link to="/" className="pp-btn-primary inline-block mt-4 px-5 py-2 rounded-lg">Back home</Link>
        </div>
      </AppLayout>
    );
  }

  const mapsUrl = hasGeo ? `https://www.openstreetmap.org/?mlat=${facility.lat}&mlon=${facility.lng}#map=16/${facility.lat}/${facility.lng}` : null;
  const sourceColor = facility.source === "seed" ? "text-[#007AFF] border-[#007AFF]/40"
    : facility.source === "osm" ? "text-[#FFD60A] border-[#FFD60A]/40"
    : "text-zinc-400 border-white/10";
  // Join once per render (cheap, O(3)) — extracted for readability + to keep filter/join out of JSX.
  const fullAddress = [facility.address, facility.city, facility.country].filter(Boolean).join(", ");
  const cityCountry = [facility.city, facility.country].filter(Boolean).join(", ");

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto" data-testid="facility-detail">
        <div className="pp-card-elevated p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2 flex-wrap mb-2">
                {facility.sport_names?.map((n) => <span key={n} className="pp-chip">{n}</span>)}
                <span className={`pp-chip ${sourceColor}`} data-testid="facility-source">{facility.source}</span>
                {facility.indoor !== null && facility.indoor !== undefined && (
                  <span className="pp-chip flex items-center gap-1">
                    {facility.indoor ? <Building2 size={11} /> : <Trees size={11} />}
                    {facility.indoor ? "Indoor" : "Outdoor"}
                  </span>
                )}
                {facility.access_type && facility.access_type !== "unknown" && (
                  <span className={`pp-chip ${facility.access_type === "public" ? "border-[#00E676]/40 text-[#00E676]" : facility.access_type === "private" ? "border-[#FF3B30]/40 text-[#FF3B30]" : "border-[#FFD60A]/40 text-[#FFD60A]"}`}
                    data-testid="facility-access-chip">
                    {facility.access_type === "semi_public" ? "Semi-public" : facility.access_type === "public" ? "Public" : "Private"}
                  </span>
                )}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black flex items-center gap-2 flex-wrap" data-testid="facility-name">
                {facility.name}
                {facility.is_claimed && (
                  <span className="pp-chip flex items-center gap-1 text-[#00E676] border-[#00E676]/40" data-testid="facility-verified-chip">
                    <ShieldCheck size={12} /> Verified
                  </span>
                )}
              </h1>
              <div className="mt-3 text-zinc-400 flex items-center gap-2">
                <MapPin size={14} className="text-[#00E676]" />
                <span>{fullAddress}</span>
              </div>
              {facility.owner_name && (
                <div className="mt-2 text-xs text-zinc-500" data-testid="facility-owner">Managed by <span className="text-white">{facility.owner_name}</span></div>
              )}
              {facility.description && (
                <p className="mt-3 text-sm text-zinc-300 max-w-2xl" data-testid="facility-description">{facility.description}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <button
                onClick={toggleFollow}
                disabled={followBusy}
                data-testid="facility-follow"
                className={`px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 ${following ? "bg-[#00E676]/15 border border-[#00E676]/40 text-[#00E676]" : "pp-btn-primary"}`}
              >
                <Heart size={14} className={following ? "fill-[#00E676]" : ""} />
                {followBusy ? "…" : following ? "Following" : "Follow venue"}
              </button>
              {isOwner ? (
                <button onClick={() => setEditOpen(true)} data-testid="facility-edit"
                  className="pp-btn-secondary px-4 py-2 rounded-lg text-xs flex items-center gap-1">
                  <Pencil size={12} /> Edit venue
                </button>
              ) : claimPending ? (
                <span className="pp-chip text-[10px] text-[#FFD60A] border-[#FFD60A]/40" data-testid="facility-claim-pending">Claim pending review</span>
              ) : canClaim ? (
                <button onClick={() => setClaimOpen(true)} data-testid="facility-claim"
                  className="pp-btn-secondary px-4 py-2 rounded-lg text-xs flex items-center gap-1">
                  <Send size={12} /> Claim this venue
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {facility.num_courts != null && (
              <Stat icon={<Trophy size={14} />} label="Courts" value={facility.num_courts} />
            )}
            <Stat icon={<Users size={14} />} label="Followers" value={facility.followers || 0} testId="facility-followers" />
            {facility.phone && (
              <a href={`tel:${facility.phone}`} className="pp-card p-3 hover:border-[#00E676]/40">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1"><Phone size={11} /> Phone</div>
                <div className="text-sm mt-1 truncate">{facility.phone}</div>
              </a>
            )}
            {facility.website && (
              <a href={facility.website} target="_blank" rel="noreferrer" className="pp-card p-3 hover:border-[#00E676]/40">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1"><Globe size={11} /> Website</div>
                <div className="text-sm mt-1 truncate text-[#00E676]">Visit <ExternalLink size={11} className="inline" /></div>
              </a>
            )}
          </div>
        </div>

        {/* Map + upcoming */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 pp-card overflow-hidden" data-testid="facility-map-wrap">
            {hasGeo ? (
              <MapContainer
                center={mapPos}
                zoom={15}
                style={{ height: "420px", width: "100%" }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={mapPos}>
                  <Popup>
                    <strong>{facility.name}</strong>
                    <br />
                    {cityCountry}
                    {mapsUrl && (
                      <>
                        <br />
                        <a href={mapsUrl} target="_blank" rel="noreferrer">Open in OpenStreetMap →</a>
                      </>
                    )}
                  </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <div className="p-10 text-center text-zinc-500">No coordinates on file for this venue.</div>
            )}
          </div>

          <div className="pp-card p-5">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><Calendar size={16} className="text-[#00E676]" /> Upcoming events</h2>
            {events.length === 0 ? (
              <div className="text-zinc-500 text-sm" data-testid="facility-no-events">No public events scheduled here yet. Be the first — pick this venue when you create one.</div>
            ) : (
              <div className="space-y-3" data-testid="facility-upcoming-list">
                {events.map((ev) => {
                  const Icon = KIND_ICON[ev.kind] || Calendar;
                  const route = KIND_ROUTE[ev.kind] || `/${ev.kind}s`;
                  const start = ev.start ? new Date(ev.start) : null;
                  const when = ev.all_day
                    ? (start ? start.toLocaleDateString() + " · All day" : "")
                    : (start ? start.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
                  return (
                    <Link key={`${ev.kind}-${ev.id}`} to={`${route}/${ev.id}`} data-testid={`facility-event-${ev.id}`}
                      className="block pp-card p-3 hover:border-[#00E676]/40 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                            <Icon size={11} /> {ev.kind}{ev.sport ? ` · ${ev.sport}` : ""}
                          </div>
                          <div className="font-medium truncate mt-0.5">{ev.title}</div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">{when}</div>
                        </div>
                        <span className="text-xs font-bold text-[#00E676] shrink-0">
                          {ev.price > 0 ? `${ev.price} ${ev.currency}` : "Free"}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {hostedClubs.length > 0 && (
            <div className="pp-card p-5" data-testid="facility-hosted-clubs-card">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Building2 size={16} className="text-[#00E676]" /> Clubs that play here
                <span className="text-xs font-normal text-zinc-500 ms-1">({hostedClubs.length})</span>
              </h2>
              <div className="space-y-2" data-testid="facility-hosted-clubs-list">
                {hostedClubs.map((c) => (
                  <Link
                    key={c.id}
                    to={`/clubs/${c.slug || c.id}`}
                    data-testid={`facility-hosted-club-${c.id}`}
                    className="block pp-card p-3 hover:border-[#00E676]/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {c.logo_url ? (
                        <img src={c.logo_url} alt={c.name} className="h-10 w-10 rounded-lg object-cover border border-white/5 shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center shrink-0">
                          <Building2 size={16} className="text-[#00E676]" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          {c.name}
                          {c.is_verified && <ShieldCheck size={12} className="text-[#00E676] shrink-0" />}
                          {c.is_private && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FFD60A]/10 text-[#FFD60A]">Private</span>}
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {[c.city, c.country].filter(Boolean).join(", ") || "—"}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        {claimOpen && <ClaimDialog facility={facility} onClose={() => setClaimOpen(false)} onSubmitted={(c) => { setClaim(c); setClaimOpen(false); }} />}
        {editOpen && <EditFacilityDialog facility={facility} onClose={() => setEditOpen(false)} onSaved={(f) => { setFacility(f); setEditOpen(false); }} />}
      </div>
    </AppLayout>
  );
}

function ClaimDialog({ facility, onClose, onSubmitted }) {
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/facilities/${facility.id}/claim`, { message, contact_phone: phone });
      toast.success("Claim submitted — we'll review and email you.");
      onSubmitted?.(data.claim);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="facility-claim-dialog">
        <DialogHeader><DialogTitle>Claim {facility.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">Tell our team how you're connected to this venue. We'll review within 1–2 business days.</p>
        <textarea data-testid="claim-message" rows={3} className="pp-input" placeholder="I'm the manager of this club, my IG is @example…"
          value={message} onChange={(e) => setMessage(e.target.value)} />
        <input data-testid="claim-phone" className="pp-input" placeholder="Contact phone (optional)"
          value={phone} onChange={(e) => setPhone(e.target.value)} />
        <DialogFooter>
          <button onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="claim-submit" className="pp-btn-primary px-4 py-2 rounded-lg">{busy ? "Submitting…" : "Submit claim"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditFacilityDialog({ facility, onClose, onSaved }) {
  const [form, setForm] = useState({
    description: facility.description || "",
    phone: facility.phone || "",
    website: facility.website || "",
    indoor: facility.indoor ?? null,
    num_courts: facility.num_courts ?? "",
    price_per_court: facility.price_per_court ?? "",
    currency: facility.currency || "EUR",
    surface: facility.surface || "",
    opening_hours: facility.opening_hours || "",
    photo_url: facility.photo_url || "",
    sport_slugs: facility.sport_slugs || [],
    sport_configs: facility.sport_configs || [],
  });
  const [allSports, setAllSports] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/sports").then((r) => setAllSports(r.data || [])).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleSport = (slug) => setForm((s) => ({
    ...s,
    sport_slugs: s.sport_slugs.includes(slug) ? s.sport_slugs.filter((x) => x !== slug) : [...s.sport_slugs, slug],
  }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ["num_courts", "price_per_court"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
        else if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      const { data } = await api.patch(`/facilities/${facility.id}`, payload);
      toast.success("Venue updated");
      onSaved?.(data.facility);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  // Selected sports as objects (for SportConfigsForm)
  const selectedSports = allSports.filter((s) => form.sport_slugs.includes(s.slug));
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="facility-edit-dialog">
        <DialogHeader><DialogTitle>Edit {facility.name}</DialogTitle></DialogHeader>
        <textarea rows={3} className="pp-input" placeholder="Short description"
          data-testid="edit-description" value={form.description} onChange={set("description")} />
        <div className="grid grid-cols-2 gap-2">
          <input className="pp-input" placeholder="Phone" data-testid="edit-phone" value={form.phone} onChange={set("phone")} />
          <input className="pp-input" placeholder="Website https://..." data-testid="edit-website" value={form.website} onChange={set("website")} />
        </div>

        {/* Multi-sport selection */}
        {allSports.length > 0 && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Sports offered</label>
            <div className="flex flex-wrap gap-1.5" data-testid="edit-sports">
              {allSports.map((s) => {
                const picked = form.sport_slugs.includes(s.slug);
                return (
                  <button
                    key={s.slug} type="button"
                    data-testid={`edit-sport-${s.slug}`}
                    onClick={() => toggleSport(s.slug)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      picked ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-white/10 text-zinc-400 hover:border-white/30"
                    }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Per-sport configuration sub-forms */}
        {selectedSports.length > 0 && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">Per-sport details</label>
            <SportConfigsForm
              selectedSports={selectedSports}
              configs={form.sport_configs}
              onChange={(cfgs) => setField("sport_configs", cfgs)}
            />
          </div>
        )}

        {/* Legacy global fields — still useful for single-sport venues */}
        <div className="pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Default details (used when a sport doesn't have its own config)</div>
          <div className="grid grid-cols-3 gap-2">
            <input type="number" min={0} max={500} className="pp-input" placeholder="Courts" data-testid="edit-num-courts" value={form.num_courts} onChange={set("num_courts")} />
            <input type="number" min={0} step={0.5} className="pp-input" placeholder="Price / court" data-testid="edit-price" value={form.price_per_court} onChange={set("price_per_court")} />
            <input className="pp-input" placeholder="Surface" data-testid="edit-surface" value={form.surface} onChange={set("surface")} />
          </div>
          <input className="pp-input mt-2" placeholder="Opening hours (e.g. Mon-Fri 7–22)"
            data-testid="edit-hours" value={form.opening_hours} onChange={set("opening_hours")} />
          <input className="pp-input mt-2" placeholder="Photo URL"
            data-testid="edit-photo" value={form.photo_url} onChange={set("photo_url")} />
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
            <input type="checkbox" checked={!!form.indoor} onChange={(e) => setForm((s) => ({ ...s, indoor: e.target.checked }))} data-testid="edit-indoor" />
            Indoor venue (default for sports without their own setting)
          </label>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="edit-save" className="pp-btn-primary px-4 py-2 rounded-lg">{busy ? "Saving…" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon, label, value, testId }) {
  return (
    <div className="pp-card p-3" data-testid={testId}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1">{icon}{label}</div>
      <div className="text-sm font-bold mt-1">{value}</div>
    </div>
  );
}
